import { useDefaultStore } from "@/shared/stores/default";
import { useToast } from "vue-toastification";
import ToastComponent from "@/components/ui/ToastComponent.vue";
import { shallowRef, unref } from "vue";
import { TransportTagStatuses, TransportConditionStatuses, RequestTagStatuses } from "@/enums";
import { useEnvVariable, useValueHelper } from "@/composables";
import { AlertModal } from "@/components/modals";

export { downloadBlob, dateStampedFilename } from "./downloadBlob";

const uid = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

/**
 * Функция для показа уведомлений (тосты)
 * @param type - success, error, warn, info
 * @param options
 */
export function toast(type, options) {
  useToast()(
    {
      component: shallowRef(ToastComponent),
      props: {
        title: options.title,
        message: options.message,
        route: options.route,
      },
    },
    {
      type,
      ...options,
    }
  );
}

export function formatNumber(num, options) {
  const value = Number(num);
  // Не финитное значение (undefined/null-as-NaN/мусор) рисовалось как "NaN" — отдаём пустую строку.
  if (!Number.isFinite(value)) return "";
  // options передаётся в КОНСТРУКТОР (Intl.format вторым аргументом их игнорирует).
  return new Intl.NumberFormat("ru-RU", options).format(value);
}

export function requestFullScreen(element) {
  let requestMethod =
    element.requestFullScreen ||
    element.webkitRequestFullScreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullScreen;

  if (requestMethod) {
    requestMethod.call(element);
  }
}

/**
 * Показ модальных окон
 * @param modal - модальное окно
 * @param options - объект с параметрами
 * @param position - center, top
 */
export function showModal(modal, options = {}, position = "center") {
  useDefaultStore().modals.push({
    id: uid(),
    component: shallowRef(modal),
    options,
    position,
  });
}

export function closeModals() {
  useDefaultStore().modals = [];
}

export function closeModalsUntil(modal) {
  // item.component хранится как shallowRef(modal), поэтому прямое сравнение с raw-компонентом
  // всегда давало false → findIndex=-1 → splice(0) стирал ВЕСЬ стек. Сравниваем через unref.
  const modals = useDefaultStore().modals;
  const index = modals.findIndex((item) => unref(item.component) === modal);

  // Если модалка не найдена — ничего не закрываем (раньше закрывалось всё).
  if (index === -1) return;

  modals.splice(index + 1);
}

export function parseValidationErrors(errorData, form, errField = "error") {
  if (errorData?.errors) {
    // Предварительно обрабатываем все ошибки для массива fuels
    // Создаем структуру для хранения ошибок по индексам
    const fuelsErrors = {};

    // Маппинг полей API на поля формы
    const fieldMapping = {
      fuel_id: "view",
      amount: "value",
      price: "price",
      tank_id: "reservoir",
      compartments: "sections",
    };

    // Сначала собираем все ошибки для массива fuels
    Object.keys(errorData.errors).forEach((key) => {
      if (key.includes("fuels.")) {
        const parts = key.split(".");
        if (parts[0] === "fuels" && !isNaN(parts[1])) {
          const index = parseInt(parts[1]);
          const field = parts[2];

          if (!fuelsErrors[index]) {
            fuelsErrors[index] = {};
          }

          // Удаляем префикс с индексом из текста ошибки
          const errorMessages = errorData.errors[key].map((errorMsg) => {
            // Заменяем префикс вида "fuels.0.fuel_id" на "fuel_id"
            return errorMsg.replace(`fuels.${index}.`, "");
          });

          fuelsErrors[index][field] = errorMessages;
        }
      }
    });

    // Проверяем, что form.fuels существует и является массивом
    if (form.fuels && Array.isArray(form.fuels)) {
      // Добавляем недостающие элементы в массив form.fuels, если необходимо
      Object.keys(fuelsErrors).forEach((indexStr) => {
        const index = parseInt(indexStr);
        if (index >= form.fuels.length) {
          // Создаем недостающие элементы массива
          while (form.fuels.length <= index) {
            form.fuels.push({
              [errField]: [],
              view: { value: null, [errField]: [] },
              value: { value: null, [errField]: [] },
              price: { value: null, [errField]: [] },
              reservoir: { value: null, [errField]: [] },
              sections: {
                1: null,
                2: null,
                3: null,
                4: null,
                5: null,
                6: null,
                [errField]: [],
              },
            });
          }
        }
      });

      // Теперь применяем ошибки к форме
      Object.keys(fuelsErrors).forEach((indexStr) => {
        const index = parseInt(indexStr);
        if (form.fuels[index]) {
          Object.keys(fuelsErrors[indexStr]).forEach((field) => {
            const formField = fieldMapping[field];
            if (formField && form.fuels[index][formField]) {
              form.fuels[index][formField][errField] = fuelsErrors[indexStr][field];
            } else if (field === "compartments") {
              // Для поля compartments добавляем ошибку в sections.error
              if (!form.fuels[index].sections[errField]) {
                form.fuels[index].sections[errField] = [];
              }
              form.fuels[index].sections[errField] = fuelsErrors[indexStr][field];
            } else {
              // Если нет прямого маппинга, добавляем в общий массив ошибок
              if (!form.fuels[index][errField]) {
                form.fuels[index][errField] = [];
              }
              form.fuels[index][errField].push(...fuelsErrors[indexStr][field]);
            }
          });
        }
      });
    }

    // Обрабатываем остальные ошибки формы
    Object.keys(errorData.errors).forEach((key) => {
      if (!key.includes(".") && form[key]) {
        form[key][errField] = errorData.errors[key];
      }
    });
  }
}

// Функция для форматирования даты в формат ДД.ММ.ГГГГ.
// Отличается от useDate().formatDate контрактом: для пустого значения возвращает
// "Не указана" (а не undefined), поэтому используется там, где нужен такой fallback.
export const formatDate = (dateString) => {
  if (!dateString) return "Не указана";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Возвращаем исходную строку, если дата некорректна

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (e) {
    console.error("Ошибка форматирования даты:", e);
    return dateString;
  }
};

// Функция для определения класса статуса заявки
export const getStatusClass = (status) => {
  switch (status) {
    case "new":
      return RequestTagStatuses.find((s) => s.id === "new");
    case "in_process":
      return RequestTagStatuses.find((s) => s.id === "in_process");
    case "in_transit":
      return RequestTagStatuses.find((s) => s.id === "in_transit");
    case "completed":
      return RequestTagStatuses.find((s) => s.id === "completed");
    case "canceled":
      return RequestTagStatuses.find((s) => s.id === "canceled");
  }
};

// Функция для получения статуса транспортного средства
export const getTransportStatus = (status) => {
  switch (status) {
    case "free":
      return TransportTagStatuses.find((s) => s.id === "free");
    case "maintenance":
      return TransportTagStatuses.find((s) => s.id === "maintenance");
    case "repair":
      return TransportTagStatuses.find((s) => s.id === "repair");
    case "in_transit":
      return TransportTagStatuses.find((s) => s.id === "in_transit");
  }
};

// Функция для получения состояния транспортного средства
export const getTransportCondition = (condition) => {
  switch (condition) {
    case "serviceable":
      return TransportConditionStatuses.find((s) => s.id === "serviceable");
    default:
      return TransportConditionStatuses.find((s) => s.id === "not_serviceable");
  }
};

// Функция для форматирования данных транспортного средства для компонента карточки
export const formatTransportData = (transport) => {
  return {
    id: transport.id,
    title: transport.model || "Не указана модель",
    year: transport.year_of_production || "Не указан",
    status: getTransportStatus(transport.status),
    driver: {
      name: transport.driver ? transport.driver.full_name : "Не назначен",
      phone: transport.driver && transport.driver.phone ? transport.driver.phone : "Не указан",
    },
    condition: getTransportCondition(transport.condition),
    type: transport.type || "Не указан",
    dopog:
      transport.driver && transport.driver.adr_issue_date
        ? formatDate(transport.driver.adr_issue_date)
        : null,
    number: {
      value: transport.registration_number || "Не указан",
      regionNumber: transport.region || "",
      regionName: transport.country || "",
    },
    pump: transport.pump,
    compartments_count: transport.compartments_count,
    compartments_sum: transport.compartments_sum,
    type_transport: transport.type_transport,
    fuel_type: transport.fuel_type,
    compartments: transport.compartments,
    image: transport.image,
  };
};

export function confirmDelete(callback, loading) {
  showModal(AlertModal, {
    title: "Вы уверены?",
    message: "Данная запись будет удалена",
    cancelBtnTitle: "Отменить",
    confirmBtnTitle: "Удалить",
    isConfirm: true,
    callback,
    loading,
  });
}

export function confirmLogout(callback, loading) {
  showModal(AlertModal, {
    title: "Вы уверены?",
    message: "Вы уверены, что хотите выйти?",
    cancelBtnTitle: "Отменить",
    confirmBtnTitle: "Выйти",
    isConfirm: true,
    callback,
    loading,
  });
}

export function removeEmptyFields(filters) {
  const { hasValue } = useValueHelper();

  return Object.fromEntries(Object.entries(filters).filter(([, value]) => hasValue(value)));
}

export function getFileUrl(file) {
  const { getEnvVar } = useEnvVariable();
  const endpoint = getEnvVar("MEDIA_ENDPOINT");
  return `${endpoint}/storage/${file.path}`;
}

export function isObject(value) {
  // typeof-проверка обязательна: для undefined/null обращение к .constructor бросало TypeError.
  return typeof value === "object" && value !== null && value.constructor === Object;
}
